import React, { useState } from 'react'
import { useDraft } from '../useDraft'
import DraftPrompt from '../components/DraftPrompt'
import { useLanguage } from '../LanguageContext'

const SERVICE_IDS = ['arrival', 'departure', 'transfer']
const SVC_TITLE = { arrival: 'svcArrivalTitle', departure: 'svcDepartureTitle', transfer: 'svcTransferTitle' }
const SVC_DESC  = { arrival: 'svcArrivalDesc',  departure: 'svcDepartureDesc',  transfer: 'svcTransferDesc' }

const VEHICLE_IDS = ['sedan', 'suv', 'van', 'maybach']
const VEH_LABEL = { sedan: 'vehicleSedanShort', suv: 'vehicleSuvShort', van: 'vehicleVanShort', maybach: 'vehicleMaybachShort' }

function ConfirmRow({ label, value }) {
  return (
    <div className="svc-confirm__row">
      <span className="svc-confirm__key">{label}</span>
      <span className="svc-confirm__val">{value}</span>
    </div>
  )
}

const EMPTY_STATE = {
  contactEmail: '',
  airport:      '',
  flightNumber: '',
  date:         '',
  selectedSvc:  [],
  passengers:   1,
  vehicle:      'sedan',
  specialReqs:  '',
}

export default function AirportServices({ onBack, member }) {
  const { t } = useLanguage()

  const [submitted,   setSubmitted]   = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [submitError, setSubmitError] = useState('')

  const [formState, setFormState] = useState({
    ...EMPTY_STATE,
    contactEmail: member?.email || '',
  })
  const { contactEmail, airport, flightNumber, date, selectedSvc, passengers, vehicle, specialReqs } = formState

  const draft = useDraft('airport_services', formState, setFormState, EMPTY_STATE)

  // After draft restore, if contactEmail ended up empty, fall back to member email
  React.useEffect(() => {
    if (!formState.contactEmail && member?.email) {
      setFormState(prev => ({ ...prev, contactEmail: member.email }))
    }
  }, [draft.draftLoading])

  const set = (key) => (val) => {
    setFormState(prev => {
      const next = { ...prev, [key]: val }
      draft.saveProgress(next)
      return next
    })
  }

  const toggleService = (id) => {
    setFormState(prev => {
      const next = {
        ...prev,
        selectedSvc: prev.selectedSvc.includes(id)
          ? prev.selectedSvc.filter(s => s !== id)
          : [...prev.selectedSvc, id],
      }
      draft.saveProgress(next)
      return next
    })
  }

  const needsVehicle = selectedSvc.includes('transfer')

  const selectedLabels = selectedSvc
    .map(id => t(SVC_TITLE[id] || id))
    .filter(Boolean)
    .join(', ')

  const handleSubmit = (e) => { e.preventDefault(); setShowConfirm(true) }

  const handleConfirm = async () => {
    setSubmitError('')
    try {
      await draft.submitForm(formState)
      setShowConfirm(false)
      setSubmitted(true)
    } catch (_) {
      setSubmitError(t('failedSubmit'))
    }
  }

  if (draft.draftLoading) {
    return (
      <div className="db-page svc-page">
        <div className="svc-submitted" style={{ opacity: 0.5 }}>
          <div className="svc-submitted__icon">◌</div>
          <p className="svc-submitted__body">{t('loading')}</p>
        </div>
      </div>
    )
  }

  if (draft.draftDecision === 'pending') {
    return (
      <div className="db-page svc-page">
        <div className="svc-header">
          <button className="svc-back" onClick={onBack} aria-label={t('back')}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M9 2L4 7l5 5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/></svg>
            {t('dashboard')}
          </button>
          <div className="svc-header__title-wrap">
            <p className="svc-header__overline">{t('serviceRequest')}</p>
            <h1 className="svc-header__title">{t('airportTitle')}</h1>
          </div>
        </div>
        <DraftPrompt draft={draft.existingDraft} serviceName={t('airportTitle')} onContinue={draft.chooseContinue} onStartNew={draft.chooseNew} />
      </div>
    )
  }

  if (submitted) {
    return (
      <div className="db-page svc-page">
        <div className="svc-submitted">
          <div className="svc-submitted__icon" aria-hidden="true">✦</div>
          <h2 className="svc-submitted__heading">{t('requestReceived')}</h2>
          <p className="svc-submitted__body">{t('requestReceivedBody')}</p>
          <p className="svc-submitted__sig">{t('signatureMwuilu')}</p>
          <button className="svc-btn-ghost" onClick={onBack}>{t('returnToDashboard')}</button>
        </div>
      </div>
    )
  }

  if (showConfirm) {
    return (
      <div className="db-page svc-page">
        <div className="svc-confirm">
          <p className="svc-confirm__overline">{t('pleaseReview')}</p>
          <h2 className="svc-confirm__heading">{t('airportTitle')}</h2>
          <div className="svc-confirm__rows">
            <ConfirmRow label={t('contactEmail')}        value={contactEmail  || '—'} />
            <ConfirmRow label={t('confirmAirport')}      value={airport       || '—'} />
            <ConfirmRow label={t('confirmFlightNumber')} value={flightNumber  || '—'} />
            <ConfirmRow label={t('date')}                value={date          || '—'} />
            <ConfirmRow label={t('confirmServices')}     value={selectedLabels || '—'} />
            <ConfirmRow label={t('passengers')}          value={`${passengers} ${passengers !== 1 ? t('passengerPlural') : t('passengerSingular')}`} />
            {needsVehicle && <ConfirmRow label={t('confirmVehicle')} value={t(VEH_LABEL[vehicle] || 'vehicleSedanShort')} />}
            {specialReqs  && <ConfirmRow label={t('specialRequirements')} value={specialReqs} />}
          </div>
          {submitError && <p className="mw-error" style={{ marginBottom: 12 }}>{submitError}</p>}
          <div className="svc-confirm__actions">
            <button type="button" className="svc-btn-ghost"   onClick={() => setShowConfirm(false)}>{t('edit')}</button>
            <button type="button" className="svc-btn-primary" onClick={handleConfirm}>{t('confirmSubmit')}</button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="db-page svc-page">
      <div className="svc-header">
        <button className="svc-back" onClick={onBack} aria-label={t('backToDashboard')}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M9 2L4 7l5 5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/></svg>
          {t('dashboard')}
        </button>
        <div className="svc-header__title-wrap">
          <p className="svc-header__overline">{t('serviceRequest')}</p>
          <h1 className="svc-header__title">{t('airportTitle')}</h1>
        </div>
      </div>

      <form className="svc-form" onSubmit={handleSubmit} noValidate>

        {/* ── Contact email ── */}
        <div className="svc-section">
          <p className="svc-section__label">{t('contactDetails')}</p>
          <div className="svc-field">
            <label className="svc-label" htmlFor="contactEmail">{t('contactEmail')}</label>
            <input
              id="contactEmail"
              type="email"
              className="svc-input"
              value={contactEmail}
              onChange={e => set('contactEmail')(e.target.value)}
              placeholder="email@example.com"
              autoComplete="email"
            />
          </div>
        </div>

        <div className="svc-section">
          <p className="svc-section__label">{t('flightDetails')}</p>
          <div className="svc-row-2">
            <div className="svc-field">
              <label className="svc-label" htmlFor="airport">{t('airportField')}</label>
              <input id="airport" type="text" className="svc-input" value={airport} onChange={e => set('airport')(e.target.value)} placeholder={t('airportNamePlaceholder')} required />
            </div>
            <div className="svc-field">
              <label className="svc-label" htmlFor="flightNum">{t('flightNumber')}</label>
              <input id="flightNum" type="text" className="svc-input" value={flightNumber} onChange={e => set('flightNumber')(e.target.value.toUpperCase())} placeholder={t('flightNumberPlaceholder')} />
            </div>
          </div>
          <div className="svc-field" style={{ marginTop: '14px' }}>
            <label className="svc-label" htmlFor="airportDate">{t('date')}</label>
            <input id="airportDate" type="date" className="svc-input svc-input--half" value={date} onChange={e => set('date')(e.target.value)} required />
          </div>
        </div>

        <div className="svc-section">
          <p className="svc-section__label">{t('serviceRequired')}</p>
          <div className="svc-service-cards">
            {SERVICE_IDS.map(svc => (
              <button key={svc} type="button" className={`svc-service-card${selectedSvc.includes(svc) ? ' svc-service-card--active' : ''}`} onClick={() => toggleService(svc)}>
                <div className="svc-service-card__check" aria-hidden="true">{selectedSvc.includes(svc) ? '✓' : ''}</div>
                <div className="svc-service-card__content">
                  <span className="svc-service-card__label">{t(SVC_TITLE[svc])}</span>
                  <span className="svc-service-card__desc">{t(SVC_DESC[svc])}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="svc-section">
          <p className="svc-section__label">{t('numberOfPassengers')}</p>
          <div className="svc-steppers">
            <div className="svc-stepper">
              <span className="svc-stepper__label">{t('passengers')}</span>
              <div className="svc-stepper__controls">
                <button type="button" className="svc-stepper__btn" onClick={() => set('passengers')(Math.max(1,  passengers - 1))} disabled={passengers <= 1} aria-label={t('decrease', { label: t('passengers') })}>−</button>
                <span className="svc-stepper__value">{passengers}</span>
                <button type="button" className="svc-stepper__btn" onClick={() => set('passengers')(Math.min(20, passengers + 1))} aria-label={t('increase', { label: t('passengers') })}>+</button>
              </div>
            </div>
          </div>
        </div>

        {needsVehicle && (
          <div className="svc-section">
            <p className="svc-section__label">{t('vehiclePreference')}</p>
            <div className="svc-vehicle-grid">
              {VEHICLE_IDS.map(v => (
                <button key={v} type="button" className={`svc-vehicle-card${vehicle === v ? ' svc-vehicle-card--active' : ''}`} onClick={() => set('vehicle')(v)}>
                  <span className="svc-vehicle-card__label">{t(VEH_LABEL[v])}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="svc-section">
          <div className="svc-field">
            <label className="svc-label" htmlFor="airportSpecial">{t('specialRequirements')}</label>
            <textarea id="airportSpecial" className="svc-textarea" rows={3} value={specialReqs} onChange={e => set('specialReqs')(e.target.value)} placeholder={t('airportSpecialPlaceholder')} />
          </div>
        </div>

        <div className="svc-form__footer">
          <button type="submit" className="svc-btn-primary svc-btn-primary--wide">{t('reviewRequest')}</button>
        </div>

      </form>
    </div>
  )
}